/* Scoped carousel and official TikTok player messages; no feed requests. */
(() => {
  'use strict';
  if (customElements.get('vn-social-carousel')) return;
  const ORIGIN = 'https://www.tiktok.com';

  class VidNaturSocialCarousel extends HTMLElement {
    connectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.initFrame = requestAnimationFrame(() => this.initialise());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.initFrame);
      this.cleanup?.();
      this.cleanup = null;
    }

    initialise() {
      if (!this.isConnected || this.cleanup) return;
      const track = this.querySelector('.vn-social-carousel__track');
      const cards = [...this.querySelectorAll('.vn-social-carousel__card')];
      const frames = [...this.querySelectorAll('.vn-social-carousel__player iframe')];
      const previous = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      const motionButton = this.querySelector('.vn-social-carousel__motion');
      if (!track || !cards.length) return;

      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const connection = navigator.connection;
      const canObserve = 'IntersectionObserver' in window;
      const configured = this.dataset.autoplay === 'true';
      const ready = new Set();
      const visibleFrames = new Set();
      const removers = [];
      let visible = false;
      let hovered = false;
      let focused = this.contains(document.activeElement);
      let pointerActive = false;
      let disposed = false;
      let autoAttempted = false;
      let timer;
      let scrollFrame;
      let focusFrame;
      let observer;
      let resizeObserver;

      const listen = (target, type, listener, options) => {
        target.addEventListener(type, listener, options);
        removers.push(() => target.removeEventListener(type, listener, options));
      };
      const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
      const automaticAllowed = () => configured && !reducedMotion.matches && !connection?.saveData;
      const send = (frame, type) => {
        if (ready.has(frame)) {
          frame.contentWindow?.postMessage({ 'x-tiktok-player': true, type }, ORIGIN);
        }
      };
      const pauseVideos = () => frames.forEach(frame => send(frame, 'pause'));
      const stopTimer = () => { clearTimeout(timer); timer = null; };
      const canAdvance = () => !disposed && automaticAllowed() && visible &&
        !document.hidden && !hovered && !focused && !pointerActive && !this.userPaused && maxScroll() > 2;

      const updateMotionButton = () => {
        if (!motionButton) return;
        const paused = Boolean(this.userPaused);
        const label = paused ? 'Reanudar carrusel' : 'Pausar carrusel';
        motionButton.setAttribute('aria-label', label);
        motionButton.setAttribute('data-paused', String(paused));
        motionButton.disabled = !automaticAllowed();
        motionButton.hidden = !configured || maxScroll() <= 2;
        const text = motionButton.querySelector('.sr-only');
        if (text) text.textContent = label;
        const icon = motionButton.querySelector('svg');
        if (icon) {
          icon.setAttribute('viewBox', '0 0 24 24');
          icon.setAttribute('fill', 'currentColor');
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML = paused
            ? '<path d="M8 5v14l11-7z"/>'
            : '<path d="M7 5h4v14H7zm6 0h4v14h-4z"/>';
        }
      };

      const updateArrows = () => {
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= maxScroll() - 2;
      };

      const move = (direction, automatic = false) => {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = cards[0].getBoundingClientRect().width + gap;
        const end = maxScroll();
        const target = automatic && track.scrollLeft >= end - 2
          ? 0 : Math.max(0, Math.min(end, track.scrollLeft + direction * step));
        track.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };

      const schedule = () => {
        stopTimer();
        if (!canAdvance()) return;
        timer = setTimeout(() => {
          timer = null;
          if (canAdvance()) move(1, true);
          schedule();
        }, 8000);
      };

      const tryAutoplay = () => {
        const first = frames[0];
        if (!first || autoAttempted || !automaticAllowed() || document.hidden ||
            !visible || !ready.has(first) || !visibleFrames.has(first) ||
            frames.includes(document.activeElement)) return;
        autoAttempted = true;
        send(first, 'mute');
        send(first, 'play');
      };

      const receive = event => {
        if (disposed || event.origin !== ORIGIN || !event.data ||
            event.data['x-tiktok-player'] !== true) return;
        const frame = frames.find(item => item.contentWindow === event.source);
        if (!frame) return;
        const { type, value } = event.data;
        const status = frame.closest('.vn-social-carousel__card')?.querySelector('.vn-social-carousel__status');
        if (type === 'onPlayerReady') {
          ready.add(frame);
          frame.dataset.playerState = 'ready';
          tryAutoplay();
        } else if (type === 'onStateChange') {
          frame.dataset.playerState = String(value);
          if (Number(value) === 1) {
            autoAttempted = true;
            if (status) status.hidden = true;
            if (document.hidden || (canObserve && !visibleFrames.has(frame))) {
              send(frame, 'pause');
            } else {
              frames.forEach(other => { if (other !== frame) send(other, 'pause'); });
            }
          }
        } else if (type === 'onPlayerError') {
          frame.dataset.playerState = 'error';
          if (status) {
            status.textContent = Number(value?.errorCode) === 3002
              ? 'Pulsa reproducir para ver este vídeo.'
              : 'Este vídeo no está disponible temporalmente.';
            status.hidden = false;
          }
        }
      };

      const refreshFocus = () => {
        cancelAnimationFrame(focusFrame);
        focusFrame = requestAnimationFrame(() => {
          if (disposed) return;
          focused = this.contains(document.activeElement);
          if (frames.includes(document.activeElement)) autoAttempted = true;
          schedule();
        });
      };

      const preferencesChanged = () => {
        if (!automaticAllowed()) pauseVideos();
        updateMotionButton();
        tryAutoplay();
        schedule();
      };

      const resized = () => { updateArrows(); updateMotionButton(); schedule(); };

      if (previous) listen(previous, 'click', () => move(-1));
      if (next) listen(next, 'click', () => move(1));
      if (motionButton) listen(motionButton, 'click', () => {
        this.userPaused = !this.userPaused;
        updateMotionButton();
        schedule();
      });
      listen(track, 'keydown', event => {
        if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      });
      listen(track, 'scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => { if (!disposed) updateArrows(); });
      }, { passive: true });
      listen(this, 'mouseenter', () => { hovered = true; stopTimer(); });
      listen(this, 'mouseleave', () => { hovered = false; schedule(); });
      listen(this, 'focusin', () => { focused = true; stopTimer(); refreshFocus(); });
      listen(this, 'focusout', refreshFocus);
      listen(window, 'blur', () => { stopTimer(); refreshFocus(); });
      listen(window, 'focus', refreshFocus);
      listen(track, 'pointerdown', () => { pointerActive = true; stopTimer(); }, { passive: true });
      const pointerEnded = () => {
        if (!pointerActive) return;
        pointerActive = false;
        schedule();
      };
      listen(window, 'pointerup', pointerEnded, { passive: true });
      listen(window, 'pointercancel', pointerEnded, { passive: true });
      listen(window, 'message', receive);
      listen(document, 'visibilitychange', () => {
        if (document.hidden) pauseVideos();
        else tryAutoplay();
        schedule();
      });
      if (reducedMotion.addEventListener) listen(reducedMotion, 'change', preferencesChanged);
      else {
        reducedMotion.addListener(preferencesChanged);
        removers.push(() => reducedMotion.removeListener(preferencesChanged));
      }
      if (connection?.addEventListener) listen(connection, 'change', preferencesChanged);

      if (canObserve) {
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.target === track) {
              visible = entry.isIntersecting && entry.intersectionRatio >= .2;
              if (!visible) pauseVideos();
            } else if (entry.isIntersecting && entry.intersectionRatio >= .35) {
              visibleFrames.add(entry.target);
            } else {
              visibleFrames.delete(entry.target);
              send(entry.target, 'pause');
            }
          });
          tryAutoplay();
          schedule();
        }, { threshold: [0, .2, .35] });
        observer.observe(track);
        frames.forEach(frame => observer.observe(frame));
      }
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(resized);
        resizeObserver.observe(track);
      } else listen(window, 'resize', resized, { passive: true });

      this.cleanup = () => {
        disposed = true;
        stopTimer();
        cancelAnimationFrame(scrollFrame);
        cancelAnimationFrame(focusFrame);
        observer?.disconnect();
        resizeObserver?.disconnect();
        pauseVideos();
        removers.forEach(remove => remove());
      };
      updateArrows();
      updateMotionButton();
    }
  }

  customElements.define('vn-social-carousel', VidNaturSocialCarousel);
})();
